import React, {
  useMemo,
  useState,
  Dispatch,
  SetStateAction,
  useEffect,
} from 'react';
import classnames from 'classnames';
import { useDispatch, useSelector } from 'react-redux';
import intl from 'react-intl-universal';
import get from 'lodash/get';
import {
  FormItem,
  TextField,
  UsersPicker,
  Typography,
  Datepicker,
  Dropdown,
  TextArea,
} from '@getsynapse/design-system';
import {
  NewProject,
  Option,
  UserOption,
  objKeyAsString,
} from 'utils/customTypes';
import { getInitialValueForDropDown } from 'utils/functions';
import { fromUtcNoonIso, toUtcNoonIso } from 'utils/calendarDates';
import { ProjectTemplate } from 'utils/types/templates';
import {
  NEW_PROJECT_FORM_FIELDS,
  PROJECT_PRIVACY,
  LONG_INPUTS_LENGTH,
  PROJECT_PRIORITY,
  SLICE_STATUS,
} from 'utils/constants';

import {
  selectAllTemplates,
  selectProjectTemplatesStatus,
} from 'state/ProjectTemplates/projectTemplatesSlice';
import { selectEnabledProjectProcesses } from 'state/Processes/processesSlice';
import {
  selectBussinessTeams,
  selectProjectCategories,
} from 'state/Organization/organizationSlice';
import { selectLearningTeams } from 'state/LearningTeams/learningTeamsSlice';
import { selectLDUsersForDropdown } from 'state/UsersManagement/usersManagementSlice';
import { MultipleOptionLI } from '../../NewProjectPage/helpers/snippets';
import ProjectCategoryPicker from 'Molecules/ProjectCategoryPicker/ProjectCategoryPicker';
import ProcessPicker from 'Molecules/ProcessPicker/ProcessPicker';
import {
  fetchTaskBundles,
  selectAllTaskBundles,
} from 'state/TasksBundle/tasksBundleSlice';
import BusinessUnitPicker from 'Molecules/BusinessUnitPicker/BusinessUnitPicker';

const ProjectCreationForm: React.FC<{
  updateValue: (newValue: objKeyAsString) => void;
  setLearningTeamIds: Dispatch<SetStateAction<string[]>>;
  data: NewProject;
  description: string;
  requestToProjectCreation?: boolean;
}> = ({
  updateValue,
  setLearningTeamIds,
  data,
  description,
  requestToProjectCreation = false,
}) => {
  const dispatch = useDispatch();
  const learningTeams = useSelector(selectLearningTeams);
  const projectCategories = useSelector(selectProjectCategories);
  const projectProcesses = useSelector(selectEnabledProjectProcesses);
  const ldUsers = useSelector(selectLDUsersForDropdown);
  const projectTemplates = useSelector(selectAllTemplates);
  const templatesStatus = useSelector(selectProjectTemplatesStatus);
  const isLoadingTemplates = templatesStatus === SLICE_STATUS.LOADING;
  const taskBundles = useSelector(selectAllTaskBundles);
  const bussinessTeams = useSelector(selectBussinessTeams);
  const currentBusinessTeams = get(
    data,
    `[${NEW_PROJECT_FORM_FIELDS.BUSINESS_TEAMS}]`
  );

  const [privacyMode, setPrivacyMode] = useState('');

  useEffect(() => {
    dispatch(fetchTaskBundles());
  }, [dispatch]);

  const privacyOptions = useMemo(
    () =>
      Object.keys(PROJECT_PRIVACY).map((key) => ({
        label: intl.get(`PROJECT_DETAIL.PRIVACY_OPTIONS.${key}`),
        value: PROJECT_PRIVACY[key],
      })),
    []
  );

  const teamOptions = useMemo(
    () =>
      learningTeams.map((team) => ({
        label: team.name,
        value: team.id,
      })),
    [learningTeams]
  );

  const ownersList = useMemo(
    () => ldUsers.filter((ldUser) => data.owners?.includes(ldUser.value)),
    [ldUsers, data]
  );

  const handlePickOwners = (owners: UserOption[]) => {
    const ownersIds = owners.map((owner) => owner.value);
    updateValue({ owners: ownersIds });
  };

  const privacyValue = useMemo(() => {
    return getInitialValueForDropDown(
      privacyOptions,
      data[NEW_PROJECT_FORM_FIELDS.PRIVACY],
      'private'
    );
  }, [privacyOptions, data]);

  const priorityOptions = useMemo(
    () =>
      Object.keys(PROJECT_PRIORITY).map((key) => ({
        label: intl.get(`PROJECT_DETAIL.PRIORITY_OPTIONS.${key}`),
        value: PROJECT_PRIORITY[key as keyof typeof PROJECT_PRIORITY],
      })),
    []
  );

  const priorityValue = useMemo(() => {
    const currentPriority = data[NEW_PROJECT_FORM_FIELDS.PRIORITY];
    const priorityForLookup =
      currentPriority &&
      currentPriority !== '' &&
      currentPriority !== 'unassigned'
        ? currentPriority
        : undefined;
    return getInitialValueForDropDown(
      priorityOptions,
      priorityForLookup,
      PROJECT_PRIORITY.MEDIUM
    );
  }, [priorityOptions, data]);

  useEffect(() => {
    if (!requestToProjectCreation) return;
    const currentPriority = data[NEW_PROJECT_FORM_FIELDS.PRIORITY];
    if (!currentPriority || currentPriority === 'unassigned') {
      const defaultPriorityValue =
        priorityValue.length > 0
          ? priorityValue[0].value
          : PROJECT_PRIORITY.MEDIUM;
      updateValue({
        [NEW_PROJECT_FORM_FIELDS.PRIORITY]: defaultPriorityValue,
      });
    }
    if (privacyValue.length > 0 && !data[NEW_PROJECT_FORM_FIELDS.PRIVACY]) {
      const privacyValueString = privacyValue[0]?.value;
      setPrivacyMode(privacyValueString);
      privacyValueString &&
        updateValue({ [NEW_PROJECT_FORM_FIELDS.PRIVACY]: privacyValueString });
    }
  }, [
    data,
    requestToProjectCreation,
    updateValue,
    priorityValue,
    privacyValue,
  ]);

  const privacyHelperText = useMemo(() => {
    switch (privacyMode) {
      case 'team':
        return intl.get('PROJECT_DETAIL.PRIVACY_TEAM_MESSAGE');
      case 'private':
        return intl.get('PROJECT_DETAIL.PRIVACY_PRIVATE_MESSAGE');
      default:
        return '';
    }
  }, [privacyMode]);
  const toSnakeCase = (inputString: string) => {
    return inputString.replace(/\s+/g, '_').toLowerCase();
  };

  const taskBundleOptions = useMemo(
    () =>
      taskBundles.map((taskBundle) => ({
        label: taskBundle.name,
        value: taskBundle.id,
      })),
    [taskBundles]
  );

  return (
    <div className='flex flex-col' data-testid='project-creation-form'>
      <Typography
        variant='body'
        weight='regular'
        className='text-neutral-black'
      >
        {description}
      </Typography>
      {projectTemplates.length > 1 &&
        description !== intl.get('DUPLICATE_PROJECT_MODAL.DESCRIPTION') && (
          <>
            <FormItem
              label={intl.get('PROJECT_DETAIL.TEMPLATES')}
              labelProps={{
                required: true,
                className: 'pt-4',
              }}
              className='w-3/6'
            >
              <Dropdown
                disabled={isLoadingTemplates}
                placeholder={intl.get('PROJECT_DETAIL.TEMPLATES_PLACEHOLDER')}
                onChange={(value) =>
                  updateValue({ [NEW_PROJECT_FORM_FIELDS.TEMPLATE]: value.id })
                }
                options={projectTemplates}
                triggerProps={{
                  'data-cy': 'project-template_dropdown',
                }}
                getOptionLabel={(option: ProjectTemplate) => option.name}
                renderOption={(
                  option: ProjectTemplate,
                  isSelected: boolean,
                  selectOption,
                  { className, ...otherProps }
                ) => (
                  <li
                    {...otherProps}
                    key={option.id}
                    data-cy={`project-template_${toSnakeCase(option.name)}`}
                    className={classnames('group', className, {
                      'hover:bg-primary focus-visible:bg-primary': isSelected,
                    })}
                    onClick={selectOption}
                  >
                    <Typography
                      weight='medium'
                      className={classnames('caption-sm', {
                        'group-hover:text-neutral-white': isSelected,
                        'group-focus-visible:text-neutral-white': isSelected,
                      })}
                    >
                      {option.name}
                    </Typography>
                    {option.description && (
                      <Typography
                        className={classnames('text-caption', {
                          'group-hover:text-neutral-white': isSelected,
                          'group-focus-visible:text-neutral-white': isSelected,
                          'text-neutral': !isSelected,
                        })}
                      >
                        {option.description}
                      </Typography>
                    )}
                  </li>
                )}
              />
            </FormItem>
            <div className='w-full border-t border-solid border-neutral-lighter my-7' />
          </>
        )}
      <div className='grid gap-y-4 gap-x-10% grid-cols-2 mt-4'>
        <FormItem>
          <TextField
            label={intl.get('PROJECT_DETAIL.PROJECT_NAME')}
            labelProps={{
              required: true,
            }}
            displayCharactersCount
            maxLength={LONG_INPUTS_LENGTH}
            value={data.title}
            onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
              updateValue({ [NEW_PROJECT_FORM_FIELDS.TITLE]: e.target.value })
            }
            placeholder={intl.get('PROJECT_DETAIL.PROJECT_NAME_PLACEHOLDER')}
            className='w-full'
            data-cy='project-input_name'
          />
        </FormItem>
        <FormItem
          label={intl.get('PROJECT_DETAIL.PROJECT_OWNERS')}
          labelProps={{
            required: true,
          }}
          className='text-primary-light font-medium'
        >
          <UsersPicker
            deactivatedUserText={intl.get('DEACTIVATED')}
            usersList={ldUsers}
            selectedUsersList={ownersList}
            onChange={handlePickOwners}
            maxLimit={6}
            required
          />
        </FormItem>
        {requestToProjectCreation && (
          <FormItem>
            <TextArea
              label={intl.get('PROJECT_DETAIL.PROJECT_DESCRIPTION')}
              labelProps={{
                required: false,
              }}
              textAreaProps={{
                defaultValue: data.description,
                'data-cy': 'project-input_description',
                className: 'overflow-y-auto',
                style: { minHeight: '126px', height: 'auto' },
              }}
              onChange={(e: React.ChangeEvent<HTMLTextAreaElement>) =>
                updateValue({
                  [NEW_PROJECT_FORM_FIELDS.DESCRIPTION]: e.target.value,
                })
              }
              className='w-full'
            />
          </FormItem>
        )}

        <div className='flex flex-col gap-y-4'>
          <FormItem>
            <Datepicker
              data-cy='project-input_date'
              className='flex justify-evenly w-full'
              canSelectRange={true}
              size='large'
              onPickDate={(dates: any) =>
                // Unlike the create/update pages, this form's consumers
                // (ProjectCreationModal, DuplicateProjectModal) dispatch form
                // state to the API without any date formatting, so the
                // UTC-noon stamp must happen here (CP-10907).
                updateValue({
                  [NEW_PROJECT_FORM_FIELDS.START_DATE]: toUtcNoonIso(
                    dates.startDate
                  ),
                  [NEW_PROJECT_FORM_FIELDS.TARGET_COMPLETION_DATE]:
                    toUtcNoonIso(dates.endDate),
                })
              }
              startDate={fromUtcNoonIso(data.startDate)}
              startDateLabel={intl.get('PROJECT_DETAIL.START_DATE')}
              startDateLabelProps={{
                required: true,
              }}
              endDate={fromUtcNoonIso(data.targetCompletionDate)}
              endDateLabel={intl.get('PROJECT_DETAIL.END_DATE')}
              endDateLabelProps={{
                required: true,
              }}
            />
          </FormItem>
          <ProcessPicker
            processes={projectProcesses}
            formItemProps={{
              label: intl.get('PROJECT_DETAIL.PROCESS'),
              labelProps: {
                required: true,
              },
            }}
            currentValue={get(data, `[${NEW_PROJECT_FORM_FIELDS.PROCESS}]`)}
            dropdownProps={{
              placeholder: intl.get('PROJECT_DETAIL.PROCESS_PLACEHOLDER'),
              onChange: (option: Option) =>
                updateValue({
                  [NEW_PROJECT_FORM_FIELDS.PROCESS]: option.value,
                }),
              triggerProps: { 'data-cy': 'project-input_process' },
            }}
          />
        </div>
        {requestToProjectCreation && (
          <BusinessUnitPicker
            formItemProps={{
              label: intl.get('PROJECT_DETAIL.BUSINESS_UNIT'),
              labelProps: {
                required: true,
              },
            }}
            currentValue={currentBusinessTeams}
            businessUnits={bussinessTeams}
            dropdownProps={{
              multiple: true,
              placeholder: intl.get('PROJECT_DETAIL.BUSINESS_UNIT_PLACEHOLDER'),
              onChange: (options: Option[]) => {
                updateValue({
                  [NEW_PROJECT_FORM_FIELDS.BUSINESS_TEAMS]: options.map(
                    (option) => option.value
                  ),
                });
              },
              listProps: { 'data-cy': 'project-business-teams' },
              triggerProps: {
                'data-cy': 'project-business-input',
              },
            }}
          />
        )}
        <ProjectCategoryPicker
          categories={projectCategories}
          currentValue={get(data, `[${NEW_PROJECT_FORM_FIELDS.CATEGORY}]`)}
          formItemProps={{
            label: intl.get('PROJECT_DETAIL.CATEGORY'),
            labelProps: {
              required: true,
            },
          }}
          dropdownProps={{
            placeholder: intl.get('PROJECT_DETAIL.CATEGORY_PLACEHOLDER'),
            onChange: (option: Option) =>
              updateValue({ [NEW_PROJECT_FORM_FIELDS.CATEGORY]: option.value }),
            triggerProps: { 'data-cy': 'project-input_category' },
          }}
        />
        {requestToProjectCreation && (
          <FormItem label={intl.get('PROJECT_DETAIL.PRIORITY')}>
            <Dropdown
              placeholder={intl.get('PROJECT_DETAIL.PRIORITY_PLACEHOLDER')}
              onChange={(option: Option) => {
                updateValue({
                  [NEW_PROJECT_FORM_FIELDS.PRIORITY]: option.value,
                });
              }}
              options={priorityOptions}
              triggerProps={{ 'data-cy': 'project-input_priority' }}
              values={priorityValue}
            />
          </FormItem>
        )}
        <FormItem label={intl.get('NEW_PROJECT_PAGE.TASK_BUNDLE.TITLE')}>
          <Dropdown
            placeholder={intl.get('NEW_PROJECT_PAGE.TASK_BUNDLE.PLACEHOLDER')}
            onChange={(option: Option) =>
              updateValue({
                [NEW_PROJECT_FORM_FIELDS.TASK_BUNDLE]: option.value,
              })
            }
            options={taskBundleOptions}
            triggerProps={{ 'data-cy': 'intake-task-bundle-dropdown' }}
          />
        </FormItem>
      </div>
      <div className='w-full border-t border-solid border-neutral-lighter my-7' />
      <div className='grid gap-y-4 gap-x-10% grid-cols-2'>
        <FormItem
          label={intl.get('PROJECT_DETAIL.PRIVACY')}
          labelProps={{
            required: true,
          }}
          helpText={privacyHelperText}
        >
          <Dropdown
            placeholder={intl.get('PROJECT_DETAIL.PRIVACY_PLACEHOLDER')}
            onChange={(option: Option) => {
              updateValue({ [NEW_PROJECT_FORM_FIELDS.PRIVACY]: option.value });
              setPrivacyMode(option.value);
            }}
            options={privacyOptions}
            triggerProps={{ 'data-cy': 'project-input_privacy' }}
            values={requestToProjectCreation ? privacyValue : []}
          />
        </FormItem>
        {privacyMode === PROJECT_PRIVACY.TEAM && (
          <FormItem
            label={intl.get('PROJECT_DETAIL.TEAM')}
            labelProps={{
              required: true,
            }}
          >
            <Dropdown
              multiple
              placeholder={intl.get('PROJECT_DETAIL.TEAM_PLACEHOLDER')}
              onChange={(options: Option[]) => {
                const ids = options.map((option) => option.value);
                setLearningTeamIds(ids);
              }}
              options={teamOptions}
              renderOption={(
                option: Option,
                isSelected: boolean,
                selectOption,
                { className, ...otherProps }
              ) => (
                <li
                  {...otherProps}
                  className={classnames('group', className, {
                    'hover:bg-primary focus-visible:bg-primary': isSelected,
                  })}
                >
                  <MultipleOptionLI
                    label={option.label}
                    isSelected={isSelected}
                    selectOption={selectOption}
                  />
                </li>
              )}
              triggerProps={{ 'data-cy': 'project-input_detail' }}
            />
          </FormItem>
        )}
      </div>
    </div>
  );
};

export default ProjectCreationForm;
